import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  Typography,
  Box,
  IconButton,
  Button,
  Input,
  InputAdornment,
  MenuItem,
  Paper,
  Select
} from '@mui/material';
import { FilterList } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Breadcrumb from 'src/components/Breadcrumb';
import { ArrowLeft } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from 'src/hooks/useDebounce';
import Filter from 'src/components/Filter/Filter';
import {
  loadSantanderClabes,
  CLEAR_SANTANDER_CLABE_OPTIONS
} from 'src/store/actions/santanderClabeActions';
import { selectSantanderClabeQueryOptions } from 'src/store/selectors/santanderClabeSelectors';
import SantanderClabeStatus from './santanderStatus.enum';
import useStyles from './SantanderClabesListView/styles';

const SearchCriteria = {
  CLIENT_NAME: 'clientName',
  CLABE: 'clabe'
};

const filters = [
  {
    title: 'COMMON.BY_STATUS',
    controlGroup: 'checkbox',
    items: [
      {
        label: 'COMMON.ASSIGNED',
        value: SantanderClabeStatus.ASSIGNED
      },
      {
        label: 'COMMON.UNASSIGNED',
        value: SantanderClabeStatus.UNASSIGNED
      }
    ]
  }
];

function getFilterOptions(options) {
  let status = null;

  if (options.assigned && !options.unassigned) {
    status = SantanderClabeStatus.ASSIGNED;
  } else if (options.unassigned && !options.assigned) {
    status = SantanderClabeStatus.UNASSIGNED;
  }

  return { status };
}

const Toolbar = ({ className, ...rest }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryOptions = useSelector(selectSantanderClabeQueryOptions);

  const [anchorEl, setAnchorEl] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [searchCriteria, setSearchCriteria] = useState(
    SearchCriteria.CLIENT_NAME
  );

  const { classes } = useStyles();

  useEffect(() => {
    return () => {
      dispatch({ type: CLEAR_SANTANDER_CLABE_OPTIONS });
    };
  }, [dispatch]);

  const debounceSearch = useDebounce(
    (search, queryOpts) =>
      dispatch(loadSantanderClabes({ ...queryOpts, ...search, page: 0 })),
    400
  );

  const handleClickFilter = useCallback(
    event => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  const handleCloseFilter = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleFilterChange = useCallback(
    event => {
      setFilterValues(event);
      const filterOptions = getFilterOptions(event);
      const search =
        searchCriteria === SearchCriteria.CLIENT_NAME
          ? { clientName: searchValue, clabe: '' }
          : { clientName: '', clabe: searchValue };
      debounceSearch(
        search,
        {
          ...queryOptions,
          page: 0,
          ...filterOptions
        }
      );
    },
    [setFilterValues, searchValue, debounceSearch, queryOptions, searchCriteria]
  );

  const handleSearch = useCallback(
    e => {
      const { value } = e.target;
      setSearchValue(value);
      const search =
        searchCriteria === SearchCriteria.CLIENT_NAME
          ? { clientName: value, clabe: '' }
          : { clientName: '', clabe: value };
      debounceSearch(
        search,
        { ...queryOptions, page: 0, ...filterValues }
      );
    },
    [setSearchValue, debounceSearch, queryOptions, filterValues, searchCriteria]
  );

  const handleSearchCriteria = useCallback(ev => {
    const newCriteria = ev.target.value;
    setSearchCriteria(newCriteria);
    setSearchValue('');
  }, []);

  return (
    <>
      <Box className={clsx(classes.root, className)} {...rest}>
        <Breadcrumb />
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box sx={{ display: 'flex' }}>
            <IconButton onClick={() => navigate(-1)} color="primary" component="span">
              <ArrowLeft />
            </IconButton>
            <Typography
              style={{ fontWeight: '400' }}
              color="primary"
              variant="h1"
            >
              {t('ADMINISTRATOR_VIEW.SANTANDER_CLABES.TITLE')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                py: 0.5,
                width: 420
              }}
            >
              <Input
                sx={{ flex: 1 }}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                }
                placeholder={
                  searchCriteria === SearchCriteria.CLIENT_NAME
                    ? t('ADMINISTRATOR_VIEW.SANTANDER_CLABES.SEARCH_BY_CLIENT_NAME')
                    : t('ADMINISTRATOR_VIEW.SANTANDER_CLABES.SEARCH_BY_CLABE')
                }
                disableUnderline
                value={searchValue}
                onChange={handleSearch}
              />
              <Select
                id="santander-clabe-search-criteria"
                variant="standard"
                value={searchCriteria}
                onChange={handleSearchCriteria}
                sx={{ ml: 1, minWidth: 140 }}
              >
                <MenuItem value={SearchCriteria.CLIENT_NAME}>
                  {t('ADMINISTRATOR_VIEW.SANTANDER_CLABES.SEARCH_BY_CLIENT_NAME')}
                </MenuItem>
                <MenuItem value={SearchCriteria.CLABE}>
                  {t('ADMINISTRATOR_VIEW.SANTANDER_CLABES.SEARCH_BY_CLABE')}
                </MenuItem>
              </Select>
            </Paper>
            <Filter
              anchorEl={anchorEl}
              filterValues={filterValues}
              handleClose={handleCloseFilter}
              handleChange={handleFilterChange}
              filters={filters}
            />
            <Button
              className={clsx(
                classes.buttons,
                classes.horizontalPadding,
                classes.outlined
              )}
              sx={{ backgroundColor: 'white', padding: '10px 20px' }}
              color="primary"
              onClick={handleClickFilter}
            >
              <FilterList className={classes.icon} />
              {t('COMMON.FILTER')}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

Toolbar.propTypes = {
  className: PropTypes.string
};

export default Toolbar;
